/**
 * SaveLoadDialogContent - Content component for save/load dialog
 */

import type { Component } from 'react';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

// Note: React.createRef is available on React object

interface SaveLoadDialogContentState {
    saves: any[];
    sortField: number;
    sortAsc: boolean;
}

class SaveLoadDialogContent extends ReactComponent<{}, SaveLoadDialogContentState> {
    private tableBodyRef: HTMLTableSectionElement | null = null;

    constructor(props: {}) {
        super(props);
        this.state = {
            saves: [],
            sortField: 0,
            sortAsc: false
        };
    }

    componentDidMount(): void {
        this.refreshSaves();
    }

    async refreshSaves(field?: number, ascending?: boolean): Promise<void> {
        const SavedGames = (window as any).SavedGames;
        if (SavedGames) {
            const saves = await SavedGames.getSortedSaves(field, ascending);
            this.setState({ saves, sortField: field ?? 0, sortAsc: ascending ?? false });
        }
    }

    formatDate(date: Date): string {
        const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

        return [
            weekdays[date.getDay()],
            months[date.getMonth()],
            date.getDate(),
            [
                date.getHours().toString().padStart(2, '0'),
                date.getMinutes().toString().padStart(2, '0'),
                date.getSeconds().toString().padStart(2, '0')
            ].join(':')
        ].join(' ');
    }

    handleNewSave = (): void => {
        // Don't close the game saves dialog - show save name dialog on top
        (window as any).handleFileSave?.(false, true, undefined, undefined, () => {
            // Callback to close game saves dialog and refresh after saving
            this.closeDialog();
            this.refreshSaves();
        });
    };

    handleExport = async (): Promise<void> => {
        // Directly trigger download without opening a dialog
        const SavedGames = (window as any).SavedGames;
        if (SavedGames) {
            const filename = SavedGames.titleForSave() + '-saves.json';
            const exportData = (await (window as any).retrieveExportData?.()) ?? '';
            const blob = new Blob([exportData], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Revoke URL after a delay
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 100);
        }
    };

    handleImport = (): void => {
        // Create a hidden file input and trigger it
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.json,.rsv,.txt';
        fileInput.style.display = 'none';
        
        fileInput.onchange = (e: any) => {
            const file = e.target.files?.[0];
            if (!file) {
                document.body.removeChild(fileInput);
                return;
            }
            
            // Read file to get save count for confirmation
            const reader = new FileReader();
            if (file.name.toLowerCase().endsWith('.rsv')) {
                // For RSV files, we can't easily count saves, so just show generic confirmation
                this.showImportConfirmation(file, 0, true);
            } else {
                reader.onload = () => {
                    try {
                        const data = JSON.parse(reader.result as string);
                        const saveCount = Array.isArray(data) ? data.length : 0;
                        this.showImportConfirmation(file, saveCount, false);
                    } catch {
                        this.showImportConfirmation(file, 0, false);
                    }
                };
                reader.readAsText(file);
            }
            
            document.body.removeChild(fileInput);
        };
        
        document.body.appendChild(fileInput);
        fileInput.click();
    };

    showImportConfirmation = (file: File, saveCount: number, isRSV: boolean): void => {
        const dialogRoot = document.getElementById('confirm-dialog-root');
        if (!dialogRoot) return;

        const React = (window as any).React;
        const ReactDOM = (window as any).ReactDOM;
        const ConfirmDialog = (window as any).ConfirmDialog;
        if (!React || !ReactDOM || !ConfirmDialog) return;

        const message = isRSV 
            ? 'This will overwrite your existing saves with the RSV file. Are you sure you want to continue?'
            : `This will overwrite your existing saves with ${saveCount} save${saveCount !== 1 ? 's' : ''} from the file. Are you sure you want to continue?`;

        const handleConfirm = () => {
            // Close confirm dialog
            if (ReactDOM.createRoot) {
                const root = (window as any).confirmDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }

            // Perform import
            this.performImport(file);
        };

        const handleCancel = () => {
            // Close confirm dialog
            if (ReactDOM.createRoot) {
                const root = (window as any).confirmDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        if (ReactDOM.createRoot) {
            let root = (window as any).confirmDialogRoot;
            if (!root) {
                root = ReactDOM.createRoot(dialogRoot);
                (window as any).confirmDialogRoot = root;
            }
            root.render(React.createElement(ConfirmDialog, {
                title: 'Import Saves',
                message: message,
                onConfirm: handleConfirm,
                onCancel: handleCancel,
                confirmText: 'Import',
                cancelText: 'Cancel',
                confirmButtonClass: 'btn'
            }));
        } else {
            ReactDOM.render(
                React.createElement(ConfirmDialog, {
                    title: 'Import Saves',
                    message: message,
                    onConfirm: handleConfirm,
                    onCancel: handleCancel,
                    confirmText: 'Import',
                    cancelText: 'Cancel',
                    confirmButtonClass: 'btn'
                }),
                dialogRoot
            );
        }
    };

    performImport = (file: File): void => {
        const reader = new FileReader();
        const SavedGames = (window as any).SavedGames;

        if (file.name.toLowerCase().endsWith('.rsv')) {
            reader.onload = async (event) => {
                try {
                    const root = await SavedGames.importRSV(event.target?.result as ArrayBuffer);
                    this.refreshSaves();
                    (window as any).handleFileSelect?.(false, '', root);
                } catch (error: any) {
                    (window as any).showAlert?.("Import Error", "Failed to import RSV file: " + (error.message || 'Unknown error'));
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            reader.onload = async () => {
                try {
                    await SavedGames.import(JSON.parse(reader.result as string));
                    await this.refreshSaves();
                    (window as any).GameUI?.showMessage?.('Saves List Imported', { type: 'success', timeout: 3.0 });
                } catch (error: any) {
                    (window as any).showAlert?.("Import Error", "Failed to import JSON file: " + (error.message || 'Unknown error'));
                }
            };
            reader.readAsText(file);
        }
    };

    handleDestroyAll = (): void => {
        const dialogRoot = document.getElementById('confirm-dialog-root');
        if (!dialogRoot) return;

        const React = (window as any).React;
        const ReactDOM = (window as any).ReactDOM;
        const ConfirmDialog = (window as any).ConfirmDialog;
        if (!React || !ReactDOM || !ConfirmDialog) return;

        const handleConfirm = async () => {
            const SavedGames = (window as any).SavedGames;
            if (SavedGames) {
                await SavedGames.reset();
                await this.refreshSaves();
            }
            // Close dialog
            if (ReactDOM.createRoot) {
                const root = (window as any).confirmDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        const handleCancel = () => {
            // Close dialog
            if (ReactDOM.createRoot) {
                const root = (window as any).confirmDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        if (ReactDOM.createRoot) {
            let root = (window as any).confirmDialogRoot;
            if (!root) {
                root = ReactDOM.createRoot(dialogRoot);
                (window as any).confirmDialogRoot = root;
            }
            root.render(React.createElement(ConfirmDialog, {
                title: 'Destroy All Saves',
                message: 'Are you sure you want to destroy all saves? This action cannot be undone.',
                onConfirm: handleConfirm,
                onCancel: handleCancel,
                confirmText: 'Destroy All',
                cancelText: 'Cancel',
                confirmButtonClass: 'btn btn-danger'
            }));
        } else {
            ReactDOM.render(
                React.createElement(ConfirmDialog, {
                    title: 'Destroy All Saves',
                    message: 'Are you sure you want to destroy all saves? This action cannot be undone.',
                    onConfirm: handleConfirm,
                    onCancel: handleCancel,
                    confirmText: 'Destroy All',
                    cancelText: 'Cancel',
                    confirmButtonClass: 'btn btn-danger'
                }),
                dialogRoot
            );
        }
    };

    handleLoad = (saveId: number): void => {
        const Globals = (window as any).Globals;
        if (!Globals?.hasUnsavedProgress) {
            this.closeDialog();
            (window as any).handleFileSelect?.(false, saveId);
            return;
        }

        const dialogRoot = document.getElementById('confirm-dialog-root');
        if (!dialogRoot) return;

        const React = (window as any).React;
        const ReactDOM = (window as any).ReactDOM;
        const ConfirmDialog = (window as any).ConfirmDialog;
        if (!React || !ReactDOM || !ConfirmDialog) return;

        const save = this.state.saves.find((s: any) => s.id === saveId);
        const saveName = save ? save.name : 'this save';

        const handleConfirm = () => {
            this.closeDialog();
            (window as any).handleFileSelect?.(false, saveId);
            
            // Close confirm dialog
            if (ReactDOM.createRoot) {
                const root = (window as any).confirmDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        const handleCancel = () => {
            // Close confirm dialog
            if (ReactDOM.createRoot) {
                const root = (window as any).confirmDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        if (ReactDOM.createRoot) {
            let root = (window as any).confirmDialogRoot;
            if (!root) {
                root = ReactDOM.createRoot(dialogRoot);
                (window as any).confirmDialogRoot = root;
            }
            root.render(React.createElement(ConfirmDialog, {
                title: 'Load Game',
                message: `Are you sure you want to load "${saveName}"? This will replace your current game state and any unsaved progress will be lost.`,
                onConfirm: handleConfirm,
                onCancel: handleCancel,
                confirmText: 'Load',
                cancelText: 'Cancel',
                confirmButtonClass: 'btn',
                confirmAutoFocus: true
            }));
        } else {
            ReactDOM.render(
                React.createElement(ConfirmDialog, {
                    title: 'Load Game',
                    message: `Are you sure you want to load "${saveName}"? This will replace your current game state and any unsaved progress will be lost.`,
                    onConfirm: handleConfirm,
                    onCancel: handleCancel,
                    confirmText: 'Load',
                    cancelText: 'Cancel',
                    confirmButtonClass: 'btn',
                    confirmAutoFocus: true
                }),
                dialogRoot
            );
        }
    };

    handleOverwrite = (saveId: number, saveName: string): void => {
        // Directly overwrite without confirmation or name dialog
        (window as any).handleFileSave?.(false, false, saveId, saveName, () => {
            this.refreshSaves();
        });
    };

    closeDialog = (): void => {
        const ReactDOM = (window as any).ReactDOM;
        const dialogRoot = document.getElementById('save-load-dialog-root');
        if (dialogRoot && ReactDOM) {
            if (ReactDOM.createRoot) {
                const root = (window as any).saveLoadDialogRoot;
                if (root) {
                    root.render(null);
                }
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        }
    };

    handleDestroy = (saveId: number): void => {
        const dialogRoot = document.getElementById('confirm-dialog-root');
        if (!dialogRoot) return;

        const React = (window as any).React;
        const ReactDOM = (window as any).ReactDOM;
        const ConfirmDialog = (window as any).ConfirmDialog;
        if (!React || !ReactDOM || !ConfirmDialog) return;

        const handleConfirm = async () => {
            const SavedGames = (window as any).SavedGames;
            if (SavedGames) {
                await SavedGames.destroySave(saveId);
                await this.refreshSaves();
            }
            // Close dialog
            if (ReactDOM.createRoot) {
                const root = (window as any).confirmDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        const handleCancel = () => {
            // Close dialog
            if (ReactDOM.createRoot) {
                const root = (window as any).confirmDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        if (ReactDOM.createRoot) {
            let root = (window as any).confirmDialogRoot;
            if (!root) {
                root = ReactDOM.createRoot(dialogRoot);
                (window as any).confirmDialogRoot = root;
            }
            root.render(React.createElement(ConfirmDialog, {
                title: 'Destroy Save',
                message: 'Are you sure you want to destroy this save? This action cannot be undone.',
                onConfirm: handleConfirm,
                onCancel: handleCancel,
                confirmText: 'Destroy',
                cancelText: 'Cancel',
                confirmButtonClass: 'btn btn-danger'
            }));
        } else {
            ReactDOM.render(
                React.createElement(ConfirmDialog, {
                    title: 'Destroy Save',
                    message: 'Are you sure you want to destroy this save? This action cannot be undone.',
                    onConfirm: handleConfirm,
                    onCancel: handleCancel,
                    confirmText: 'Destroy',
                    cancelText: 'Cancel',
                    confirmButtonClass: 'btn btn-danger'
                }),
                dialogRoot
            );
        }
    };

    handleRename = (saveId: number, currentName: string): void => {
        // Open rename dialog
        const dialogRoot = document.getElementById('rename-save-dialog-root');
        if (!dialogRoot) return;

        const React = (window as any).React;
        const ReactDOM = (window as any).ReactDOM;
        const RenameSaveDialog = (window as any).RenameSaveDialog;
        if (!React || !ReactDOM || !RenameSaveDialog) return;

        const handleRename = async (newName: string) => {
            const SavedGames = (window as any).SavedGames;
            if (SavedGames) {
                await SavedGames.renameSave(saveId, newName);
                await this.refreshSaves();
            }
            
            // Close rename dialog (but NOT the game saves dialog)
            if (ReactDOM.createRoot) {
                const root = (window as any).renameSaveDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        const handleCancel = () => {
            // Close rename dialog (but NOT the game saves dialog)
            if (ReactDOM.createRoot) {
                const root = (window as any).renameSaveDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        if (ReactDOM.createRoot) {
            let root = (window as any).renameSaveDialogRoot;
            if (!root) {
                root = ReactDOM.createRoot(dialogRoot);
                (window as any).renameSaveDialogRoot = root;
            }
            root.render(React.createElement(RenameSaveDialog, {
                currentName: currentName,
                onRename: handleRename,
                onCancel: handleCancel
            }));
        } else {
            ReactDOM.render(
                React.createElement(RenameSaveDialog, {
                    currentName: currentName,
                    onRename: handleRename,
                    onCancel: handleCancel
                }),
                dialogRoot
            );
        }
    };

    handleSortClick = (fieldIndex: number): void => {
        let newField = this.state.sortField;
        let newAsc = this.state.sortAsc;
        if (fieldIndex === this.state.sortField) {
            newAsc = !this.state.sortAsc;
        } else {
            newField = fieldIndex;
            newAsc = true;
        }
        this.setState({ sortField: newField, sortAsc: newAsc });
        this.refreshSaves(newField, newAsc);
    };

    render(): any {
        const SavedGames = (window as any).SavedGames;
        if (!SavedGames) return null;

        const PaginatedTable = (window as any).PaginatedTable;
        const hasPagination = PaginatedTable && this.state.saves.length > 0;

         const renderRow = (save: any, index: number) => {
            if (!save) {
                // Empty row
                return React.createElement('tr', { 
                    key: `empty-${index}`,
                    className: 'empty-row'
                },
                    React.createElement('td', { colSpan: 5, style: { height: '64px' } })
                );
            }
            
            const isQuickSave = save.id === 0;
            
            return React.createElement('tr', { key: save.id },
                React.createElement('td', null,
                    React.createElement('button', {
                        className: 'btn load-save',
                        onClick: () => this.handleLoad(save.id)
                    }, 'Load')
                ),
                React.createElement('td', {
                    style: {
                        width: '60px',
                        textAlign: 'center'
                    }
                }, String(save.id)),
                React.createElement('td', null,
                    React.createElement('div', {
                        style: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%'
                        }
                    },
                        React.createElement('span', {
                            style: {
                                flex: '1',
                                padding: '4px 8px',
                                textAlign: 'left',
                                color: 'var(--main-text-color)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0, // Allow flex item to shrink below content size
                                background: 'transparent'
                            }
                        }, save.name),
                        !isQuickSave && React.createElement('button', {
                            className: 'btn rename-save',
                            style: { 
                                marginLeft: '8px',
                                flexShrink: 0
                            },
                            onClick: () => {
                                this.handleRename(save.id, save.name);
                            }
                        }, 'Rename')
                    )
                ),
                React.createElement('td', {
                    style: {
                        lineHeight: '1.2rem',
                        whiteSpace: 'nowrap'
                    }
                }, this.formatDate(new Date(save.date))),
                React.createElement('td', null,
                    React.createElement('div', {
                        style: {
                            display: 'flex',
                            gap: '8px',
                            justifyContent: 'flex-end'
                        }
                    },
                        !isQuickSave && React.createElement('button', {
                            className: 'btn overwrite-save',
                            onClick: () => this.handleOverwrite(save.id, save.name),
                            disabled: (window as any).GameUI?.saveDisabled
                        }, 'Overwrite'),
                        React.createElement('button', {
                            className: 'btn btn-danger destroy-save',
                            onClick: () => this.handleDestroy(save.id)
                        }, 'Destroy')
                    )
                )
            );
        };

        const colgroup = React.createElement('colgroup', null,
            React.createElement('col', { style: { width: '80px' } }), // Load button
            React.createElement('col', { style: { width: '60px' } }), // ID
            React.createElement('col', { style: { width: '100%' } }), // Name
            React.createElement('col', { style: { width: '260px' } }), // Date
            React.createElement('col', { style: { width: '180px' } }) // Actions
        );

        // Sort indication classes for header cells
        const thIdClass = this.state.sortField === 0 ? (this.state.sortAsc ? 'sort-asc' : 'sort-desc') : '';
        const thNameClass = this.state.sortField === 1 ? (this.state.sortAsc ? 'sort-asc' : 'sort-desc') : '';
        const thDateClass = this.state.sortField === 2 ? (this.state.sortAsc ? 'sort-asc' : 'sort-desc') : '';

        const tableHeaders = React.createElement('thead', null,
            React.createElement('tr', null,
                React.createElement('th', null),
                React.createElement('th', {
                    'data-field': '0',
                    className: thIdClass,
                    style: { textAlign: 'center' },
                    onClick: () => this.handleSortClick(0)
                }, 'ID'),
                React.createElement('th', {
                    'data-field': '1',
                    className: thNameClass,
                    onClick: () => this.handleSortClick(1)
                }, 'Name'),
                React.createElement('th', {
                    'data-field': '2',
                    className: thDateClass,
                    onClick: () => this.handleSortClick(2)
                }, 'Date'),
                React.createElement('th', { style: { textAlign: 'right' } })
            )
        );

        return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', height: '100%' } },
            // Header buttons
            React.createElement('div', { className: 'savegames-header' },
                React.createElement('button', {
                    id: 'new_savegame',
                    className: 'btn',
                    onClick: this.handleNewSave,
                    disabled: (window as any).GameUI?.saveDisabled
                }, 'Create a New Save'),
                React.createElement('button', {
                    className: 'btn export_savegames',
                    onClick: this.handleExport
                }, 'Export Saves'),
                React.createElement('button', {
                    className: 'btn import_savegames',
                    onClick: this.handleImport
                }, 'Import Saves'),
                React.createElement('button', {
                    className: 'btn btn-danger destroy_savegames',
                    onClick: this.handleDestroyAll
                }, 'Destroy All Saves')
            ),
            // Table with pagination (always use pagination if available)
            hasPagination
                ? React.createElement(PaginatedTable, {
                    key: 'savegames-table',
                    items: this.state.saves.length > 0 ? this.state.saves : [],
                    renderRow: renderRow,
                    headers: tableHeaders,
                    colgroup: colgroup,
                    minItemsPerPage: 5,
                    rowHeight: 64,
                    maxHeight: 'calc(100vh - 300px)',
                    className: 'savegames-table'
                })
                : React.createElement('div', {
                    style: {
                        flex: '1 1 auto',
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }
                },
                    React.createElement('table', { className: 'savegames-table', style: { width: '100%', borderCollapse: 'collapse' } },
                        tableHeaders,
                        React.createElement('tbody', { className: 'save-load-table-body' },
                            this.state.saves.length === 0
                                ? React.createElement('tr', null,
                                    React.createElement('td', { colSpan: 5, style: { textAlign: 'center', padding: '20px' } },
                                        'No saves found'
                                    )
                                  )
                                : this.state.saves.map((save: any, index: number) => renderRow(save, index))
                        )
                    )
                )
        );
    }
}

(window as any).SaveLoadDialogContent = SaveLoadDialogContent;
export default SaveLoadDialogContent;
